import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, resolveComponent as _resolveComponent, unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, withCtx as _withCtx, createBlock as _createBlock, renderList as _renderList, Fragment as _Fragment, withModifiers as _withModifiers } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "bg-modal",
  style: {"width":"30vw","height":"42rem"}
}
const _hoisted_2 = {
  key: 1,
  class: "bg-modal",
  style: {"width":"30vw"}
}
const _hoisted_3 = { class: "row-top" }
const _hoisted_4 = { class: "font-m size-m mleft-04 main-m" }
const _hoisted_5 = { class: "bg-modal w-maxC" }
const _hoisted_6 = { class: "row-top gap-12 mtop-04" }
const _hoisted_7 = { class: "example-col-small-w" }
const _hoisted_8 = { class: "example-col-w" }
const _hoisted_9 = { class: "example-col-w" }
const _hoisted_10 = {
  key: 0,
  class: "row w-full mbottom-02"
}
const _hoisted_11 = { class: "bg-modal w-maxC" }
const _hoisted_12 = { class: "col gap-12 scrollbox" }
const _hoisted_13 = {
  key: 1,
  class: "mbottom-10"
}
const _hoisted_14 = { class: "row gap-04 mleft-03" }

import loadingSpinner from "@/views/loadingSpinner.vue";
import editDocumentFieldPreferencePanel from "./editDocumentFieldPreferencePanel.vue";

import { ref, computed, nextTick, onMounted } from 'vue';
import { getTemplateVariables } from '@/functions/documentSuggestions';
import gen from "@/functions/general.js";
import { useI18n } from 'vue-i18n';
import { useCurrentEntryStore } from '@/stores/currentEntry';
import { createReferenceSheetTitle } from "@/functions/resultSuggestion.js";
import appuserapi from "@/api/appuserapi";


export default /*@__PURE__*/_defineComponent({
  __name: 'editDocumentFieldPreference',
  props: {
  uiSelectorData : {
    type: Object,
    required: false,
  }
},
  emits: ["update", "updateAutoUseFirst"],
  setup(__props, { emit: __emit }) {

const currentEntryStore = useCurrentEntryStore();
const { t } = useI18n();

const props = __props;

const emits = __emit;

const uiSelector = computed(() => props.uiSelectorData.uiSelector)

const hasEditableText = computed(() =>
  ['TextLineUi', 'TextAreaUi', 'NumberUi'].includes(uiSelector.value.uiType)
)
const hasListUi = computed(() => ['ListUi'].includes(uiSelector.value.uiType))

const currRefSheet = computed(() => currentEntryStore.middleRefSheet ?? null)
const refSheetTitle = computed(() =>
  currRefSheet.value ? createReferenceSheetTitle(currRefSheet.value) : ""
)
const currReferenceDocument = computed(() =>
  currentEntryStore.middleReferenceDocument ?? null
)

const dropd = ref(null)

// Which panel last had focus — used for the shared "Add variable" dropdown.
const activePanel = ref<'default' | 'custom'>('default')

const defaultPanelRef = ref(null)
const customPanelRef = ref(null)

// ---------- Default panel initial values ----------
const defaultSuggestionsRaw = uiSelector.value.documentFieldPreference?.suggestions
const initialDefaultListDataTable = (() => {
  if (!hasListUi.value) return [];
  if (defaultSuggestionsRaw) return JSON.parse(defaultSuggestionsRaw);
  return props.uiSelectorData.dataTable ?? [];
})()

const initialDefaultSuggestions =
  uiSelector.value.documentFieldPreference?.suggestions || uiSelector.value.textSuggestions || ''
const initialDefaultAutoUseFirst =
  uiSelector.value.documentFieldPreference?.autoUseFirst ?? false

// ---------- Custom panel initial values (filled by initialize()) ----------
const customPref = ref<{
  documentFieldPreferenceId: number;
  suggestions: string | null;
  autoUseFirst: boolean;
} | null>(null)

const hasCustom = computed(() => customPref.value !== null)

const initialCustomListDataTable = ref([])
const initialCustomSuggestions = ref('')
const initialCustomAutoUseFirst = ref(false)

const loaded = ref(false)

async function initialize() {
  if (!currentEntryStore.middleReferenceDocument) {
    loaded.value = true
    return
  }
  const referenceDocumentId = currentEntryStore.middleReferenceDocument.id
  const uiSelectorId = props.uiSelectorData.uiSelector.uiSelectorId

  try {
    const pref = await appuserapi.getReferenceDocumentFieldPreference(referenceDocumentId, uiSelectorId)
    customPref.value = pref
    const raw = pref.suggestions

    if (hasListUi.value) {
      // Start from the canonical full list of options
      const fullList = gen.deepCopy(props.uiSelectorData.dataTable ?? [])
      if (raw) {
        const savedRows = JSON.parse(raw)
        // Overlay the saved `selected` state on top of the full list
        const savedById = new Map(savedRows.map((r: any) => [r.id, r.selected]))
        fullList.forEach((row: any) => {
          if (savedById.has(row.id)) row.selected = savedById.get(row.id)
        })
      }
      initialCustomListDataTable.value = fullList
    }
    initialCustomSuggestions.value = raw ?? ''
    initialCustomAutoUseFirst.value = pref.autoUseFirst
  } catch {
    // no custom preference — leave customPref null
  } finally {
    loaded.value = true
  }
}

// ---------- Persist handlers ----------
function onDefaultPersist(newValue: string) {
  emits('update', uiSelector.value, newValue)
}
function onDefaultAutoUseFirst(newValue: boolean) {
  emits('updateAutoUseFirst', uiSelector.value, newValue)
}
async function onCustomPersist(newValue: string) {
  const referenceDocumentId = currentEntryStore.middleReferenceDocument.id
  const uiSelectorId = props.uiSelectorData.uiSelector.uiSelectorId
  await appuserapi.updateReferenceDocumentFieldPreference(
    referenceDocumentId,
    uiSelectorId,
    { suggestions: newValue }
  )
}
async function onCustomAutoUseFirst(newValue: boolean) {
  const referenceDocumentId = currentEntryStore.middleReferenceDocument.id
  const uiSelectorId = props.uiSelectorData.uiSelector.uiSelectorId
  await appuserapi.updateReferenceDocumentFieldPreference(
    referenceDocumentId,
    uiSelectorId,
    { autoUseFirst: newValue }
  )
}

function insertVariable(key: string) {
  const target = activePanel.value === 'custom' ? customPanelRef.value : defaultPanelRef.value
  target?.insertVariable(key)
  dropd.value = ""
}

onMounted(async () => {
  await initialize()
  await nextTick()
  if (hasCustom.value) {
    customPanelRef.value?.focusInput()
    activePanel.value = 'custom'
  } else {
    defaultPanelRef.value?.focusInput()
    activePanel.value = 'default'
  }
})

return (_ctx: any,_cache: any) => {
  const _component_text_item = _resolveComponent("text-item")!
  const _component_svg_item = _resolveComponent("svg-item")!
  const _component_buttonGroup = _resolveComponent("buttonGroup")!
  const _component_input_box = _resolveComponent("input-box")!
  const _component_dropdownBox = _resolveComponent("dropdownBox")!
  const _component_list_group = _resolveComponent("list-group")!

  return (!loaded.value)
    ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
        _createVNode(loadingSpinner, {
          logo: false,
          style: 'w-3',
          class: "mtop-50"
        })
      ]))
    : (_openBlock(), _createElementBlock("div", _hoisted_2, [
        _createElementVNode("div", _hoisted_3, [
          _createVNode(_component_text_item, {
            txt: uiSelector.value.uiSelectorLabel,
            class: "mleft-04 title-max-w",
            styles: "font-l main-h",
            style: {"margin-top":"-0.2rem"}
          }, null, 8, ["txt"]),
          (hasEditableText.value)
            ? (_openBlock(), _createBlock(_component_dropdownBox, {
                key: 0,
                modelValue: dropd.value,
                "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((dropd).value = $event)),
                openOn: "SyntaxInfo",
                class: "mleft-auto"
              }, {
                dropd: _withCtx(() => [
                  _createElementVNode("div", _hoisted_5, [
                    _createElementVNode("div", _hoisted_6, [
                      _createElementVNode("div", _hoisted_7, [
                        _createVNode(_component_text_item, {
                          txt: _unref(t)('patient.document.suggestions.examples.lines.title'),
                          styles: "font-mBold",
                          class: "mleft-04"
                        }, null, 8, ["txt"]),
                        _createVNode(_component_text_item, {
                          txt: _unref(t)('patient.document.suggestions.examples.lines.subtitle'),
                          styles: "font-m main-l",
                          class: "mleft-04"
                        }, null, 8, ["txt"]),
                        _createVNode(_component_input_box, { class: "mtop-03" }, {
                          default: _withCtx(() => [
                            _createVNode(_component_text_item, {
                              txt: _unref(t)('patient.document.suggestions.examples.lines.example'),
                              class: "linebreak pad-m mleft-02 main-m cursor-sel"
                            }, null, 8, ["txt"])
                          ]),
                          _: 1
                        })
                      ]),
                      _createElementVNode("div", _hoisted_8, [
                        _createVNode(_component_text_item, {
                          txt: _unref(t)('patient.document.suggestions.examples.blocks.title'),
                          styles: "font-mBold",
                          class: "mleft-04"
                        }, null, 8, ["txt"]),
                        _createVNode(_component_text_item, {
                          txt: _unref(t)('patient.document.suggestions.examples.blocks.subtitle'),
                          styles: "font-m main-l",
                          class: "mleft-04"
                        }, null, 8, ["txt"]),
                        _createVNode(_component_input_box, { class: "mtop-03" }, {
                          default: _withCtx(() => [
                            _createVNode(_component_text_item, {
                              txt: _unref(t)('patient.document.suggestions.examples.blocks.example'),
                              class: "linebreak pad-m mleft-02 main-m cursor-sel"
                            }, null, 8, ["txt"])
                          ]),
                          _: 1
                        })
                      ]),
                      _createElementVNode("div", _hoisted_9, [
                        _createVNode(_component_text_item, {
                          txt: _unref(t)('patient.document.suggestions.examples.titledBlocks.title'),
                          styles: "font-mBold",
                          class: "mleft-04"
                        }, null, 8, ["txt"]),
                        _createVNode(_component_text_item, {
                          txt: _unref(t)('patient.document.suggestions.examples.titledBlocks.subtitle'),
                          styles: "font-m main-l",
                          class: "mleft-04"
                        }, null, 8, ["txt"]),
                        _createVNode(_component_input_box, { class: "mtop-03" }, {
                          default: _withCtx(() => [
                            _createVNode(_component_text_item, {
                              txt: _unref(t)('patient.document.suggestions.examples.titledBlocks.example'),
                              class: "linebreak pad-m mleft-02 main-m cursor-sel"
                            }, null, 8, ["txt"])
                          ]),
                          _: 1
                        })
                      ])
                    ])
                  ])
                ]),
                default: _withCtx(() => [
                  _createVNode(_component_buttonGroup, { class: "pad-s psides-04" }, {
                    default: _withCtx(() => [
                      _createVNode(_component_svg_item, {
                        icon: "help",
                        styles: "size-m main-l"
                      }),
                      _createElementVNode("span", _hoisted_4, _toDisplayString(_unref(t)('patient.document.suggestions.examplesTitle')), 1)
                    ]),
                    _: 1
                  })
                ]),
                _: 1
              }, 8, ["modelValue"]))
            : _createCommentVNode("", true)
        ]),
        (hasEditableText.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_10, [
              _createVNode(_component_dropdownBox, {
                modelValue: dropd.value,
                "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((dropd).value = $event)),
                openOn: "AddVariable",
                class: "mleft-auto"
              }, {
                dropd: _withCtx(() => [
                  _createElementVNode("div", _hoisted_11, [
                    _createElementVNode("div", _hoisted_12, [
                      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(getTemplateVariables)(), (section, index) => {
                        return (_openBlock(), _createElementBlock("div", {
                          key: index,
                          class: "w-full"
                        }, [
                          _createVNode(_component_text_item, {
                            txt: section.section,
                            styles: "font-mBold",
                            class: "mleft-01"
                          }, null, 8, ["txt"]),
                          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(section.variables, (variable) => {
                            return (_openBlock(), _createBlock(_component_list_group, {
                              key: variable.key,
                              class: "pvertical-01",
                              onClick: _withModifiers(($event: any) => (insertVariable(variable.key)), ["stop"])
                            }, {
                              default: _withCtx(() => [
                                _createVNode(_component_text_item, {
                                  txt: variable.description
                                }, null, 8, ["txt"])
                              ]),
                              _: 2
                            }, 1032, ["onClick"]))
                          }), 128))
                        ]))
                      }), 128))
                    ])
                  ])
                ]),
                default: _withCtx(() => [
                  _createVNode(_component_buttonGroup, {
                    styles: "pad-s border-m",
                    class: "psides-06"
                  }, {
                    default: _withCtx(() => [
                      _createVNode(_component_text_item, {
                        txt: _unref(t)('patient.document.suggestions.addVariable')
                      }, null, 8, ["txt"])
                    ]),
                    _: 1
                  })
                ]),
                _: 1
              }, 8, ["modelValue"])
            ]))
          : (_openBlock(), _createElementBlock("div", _hoisted_13)),
        (hasCustom.value)
          ? (_openBlock(), _createBlock(editDocumentFieldPreferencePanel, {
              key: 2,
              ref_key: "customPanelRef",
              ref: customPanelRef,
              class: "mbottom-20",
              uiSelector: uiSelector.value,
              initialListDataTable: initialCustomListDataTable.value,
              initialSuggestions: initialCustomSuggestions.value,
              initialAutoUseFirst: initialCustomAutoUseFirst.value,
              onPersistSuggestions: onCustomPersist,
              onUpdateAutoUseFirst: onCustomAutoUseFirst,
              onFocusInput: _cache[2] || (_cache[2] = ($event: any) => (activePanel.value = 'custom'))
            }, {
              title: _withCtx(() => [
                _createElementVNode("div", _hoisted_14, [
                  _createVNode(_component_text_item, {
                    txt: refSheetTitle.value,
                    styles: "font-mBold main-h"
                  }, null, 8, ["txt"]),
                  _createVNode(_component_text_item, {
                    txt: "-",
                    styles: "font-mBold main-h"
                  }),
                  _createVNode(_component_text_item, {
                    txt: currReferenceDocument.value.title,
                    styles: "font-mBold main-h"
                  }, null, 8, ["txt"])
                ])
              ]),
              _: 1
            }, 8, ["uiSelector", "initialListDataTable", "initialSuggestions", "initialAutoUseFirst"]))
          : _createCommentVNode("", true),
        _createVNode(editDocumentFieldPreferencePanel, {
          ref_key: "defaultPanelRef",
          ref: defaultPanelRef,
          uiSelector: uiSelector.value,
          initialListDataTable: _unref(initialDefaultListDataTable),
          initialSuggestions: _unref(initialDefaultSuggestions),
          initialAutoUseFirst: _unref(initialDefaultAutoUseFirst),
          onPersistSuggestions: onDefaultPersist,
          onUpdateAutoUseFirst: onDefaultAutoUseFirst,
          onFocusInput: _cache[3] || (_cache[3] = ($event: any) => (activePanel.value = 'default'))
        }, {
          title: _withCtx(() => [
            _createVNode(_component_text_item, {
              txt: _unref(t)('common.default'),
              styles: "font-mBold main-h",
              class: "mleft-03"
            }, null, 8, ["txt"])
          ]),
          _: 1
        }, 8, ["uiSelector", "initialListDataTable", "initialSuggestions", "initialAutoUseFirst"])
      ]))
}
}

})